import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, PackageCheck, Undo2, X } from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";

const STATUS_META = {
  requested: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  approved: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-400",
  received: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  refunded: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
};

export default function AdminReturns() {
  const [returns, setReturns] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [refundFor, setRefundFor] = useState(null); // return id
  const [refundAmount, setRefundAmount] = useState("");

  const load = useCallback(() => {
    api.get("/admin/returns")
      .then(({ data }) => setReturns(data.returns))
      .catch((e) => toast.error(apiError(e, "Couldn't load returns")));
  }, []);
  useEffect(load, [load]);

  const act = async (ret, patch) => {
    if (busyId) return;
    setBusyId(ret.id);
    try {
      await api.patch(`/admin/returns/${ret.id}`, patch);
      toast.success(`Return ${ret.return_number} → ${patch.status}`);
      setRefundFor(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update the return"));
    } finally {
      setBusyId(null);
    }
  };

  const input = "h-9 w-28 rounded-lg border border-white/10 bg-black/40 px-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

  return (
    <div data-testid="admin-returns" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><Undo2 className="h-6 w-6 text-amber-400" /> Returns</h1>
        <p className="mt-1 text-sm text-neutral-500">Approve, receive and refund customer returns. Receiving restocks inventory through the ledger.</p>
      </div>

      {!returns ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : returns.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="returns-empty">
          No return requests yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/[0.07]">
          <table className="w-full text-left text-sm" data-testid="returns-table">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3">Return</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Refund</th>
                <th className="px-4 py-3">Audit</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {returns.map((r) => (
                <tr key={r.id} className="border-b border-white/[0.05] align-top hover:bg-white/[0.02]" data-testid={`return-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-amber-400">{r.return_number}</td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-400">{r.order_number}</td>
                  <td className="max-w-44 px-4 py-3">
                    <p className="truncate text-neutral-200" title={r.product_name}>{r.product_name}</p>
                    <p className="text-xs text-neutral-500">{r.sku} · qty {r.qty}</p>
                  </td>
                  <td className="max-w-52 px-4 py-3 text-xs text-neutral-400" title={r.reason}>{r.reason}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_META[r.status]}`}
                      data-testid={`return-status-${r.id}`}>{r.status}</span>
                    {r.inventory_adjusted && (
                      <p className="mt-1 text-[10px] font-semibold text-emerald-400" data-testid={`return-restocked-${r.id}`}>Restocked ✓</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-400">
                    {r.refund_amount ? <>{fmt(r.refund_amount)} · <span className={r.refund_status === "refunded" ? "text-emerald-400" : "text-amber-300"}>{r.refund_status}</span></> : "—"}
                  </td>
                  <td className="px-4 py-3 text-[10px] leading-relaxed text-neutral-600">
                    <p>{r.created_by}</p>
                    <p>{(r.updated_at || r.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {r.status === "requested" && (
                        <>
                          <button onClick={() => act(r, { status: "approved" })} disabled={busyId === r.id} data-testid={`return-approve-${r.id}`}
                            className="flex h-8 items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-3 text-[11px] font-bold text-sky-300 hover:bg-sky-500/20 disabled:opacity-40">
                            <Check className="h-3 w-3" /> Approve
                          </button>
                          <button onClick={() => act(r, { status: "rejected" })} disabled={busyId === r.id} data-testid={`return-reject-${r.id}`}
                            className="flex h-8 items-center gap-1 rounded-full border border-red-500/40 px-3 text-[11px] font-bold text-red-300 hover:bg-red-500/10 disabled:opacity-40">
                            <X className="h-3 w-3" /> Reject
                          </button>
                        </>
                      )}
                      {r.status === "approved" && (
                        <button onClick={() => act(r, { status: "received", restock: true })} disabled={busyId === r.id} data-testid={`return-receive-${r.id}`}
                          className="flex h-8 items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 text-[11px] font-bold text-violet-300 hover:bg-violet-500/20 disabled:opacity-40">
                          <PackageCheck className="h-3 w-3" /> Mark received (restock)
                        </button>
                      )}
                      {r.status === "received" && (
                        refundFor === r.id ? (
                          <div className="flex items-center gap-1.5">
                            <input type="number" min="0" step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                              placeholder="₹ amount" data-testid={`return-refund-amount-${r.id}`} className={input} />
                            <button onClick={() => act(r, { status: "refunded", refund_amount: parseFloat(refundAmount) || 0 })}
                              disabled={busyId === r.id} data-testid={`return-refund-confirm-${r.id}`}
                              className="h-8 rounded-full bg-emerald-500 px-3 text-[11px] font-bold text-black hover:bg-emerald-400 disabled:opacity-40">
                              Confirm
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => { setRefundFor(r.id); setRefundAmount(""); }} data-testid={`return-refund-${r.id}`}
                            className="flex h-8 items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20">
                            Process refund
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
